import { Injectable } from '@angular/core';
import { firstValueFrom, map, Observable } from 'rxjs';
import { Routes } from '../config/routes';
import { LegFiJwtService } from './auth/legfi-jwt.service';
import { ApplicationHttpClient } from '../components/shared/http/application-http-client';

export interface AIRequest {
    query: string;
    context?: string;
    system?: any[];
    thread?: any[];
    _isEditingFullContent?: boolean;
}

export interface AIResponse {
    response?: string;
    error?: string;
    isEditingFullContent?: boolean;
}

export interface AIChatbotResponse {
    success: boolean;
    response?: string;
    sources?: PennySource[];
    context_used?: boolean;
    chunks_count?: number;
    chatbot_type?: string;
    message?: string; // For error responses
}

export interface PennySource {
    type: string;
    title: string;
    url: string | null;
}

export interface PennyTesterResponse {
    conversationId: string;
    answer: string;
    sources: PennySource[];
}

@Injectable({
    providedIn: 'root',
})
export class AIService {
    private baseUrl = 'http://localhost';

    constructor(
        private _legFiJwtService: LegFiJwtService,
        private applicationHttp: ApplicationHttpClient
    ) {}

    async makeRequest(request: AIRequest): Promise<string> {
        const jwt = this._legFiJwtService.readOrRedirect();
        if (!jwt || !jwt.orgId) {
            return '❌ Error: Authentication required for AI features.';
        }

        const requestBody = {
            query: request.query,
            context: request.context,
            system: request.system || [],
            thread: request.thread || [],
            _isEditingFullContent: request._isEditingFullContent || false
        };

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.TinyMCEAIProxy(jwt.orgId));

        try {
            const data = await firstValueFrom(
                this.applicationHttp.post<AIResponse>(url, JSON.stringify(requestBody))
            );

            if (data.response) {
                // Store the full content replacement flag for later use
                if (data.isEditingFullContent) {
                    (window as any)._tinyMCE_isEditingFullContent = true;
                } else {
                    (window as any)._tinyMCE_isEditingFullContent = false;
                }
                return data.response;
            } else if (data.error) {
                throw new Error(data.error);
            }
            throw new Error('No response from AI service');
        } catch (error: any) {
            console.error('AI Request Error:', error);
            if (error.status === 401) {
                return '❌ Error: Authentication required. Please log in again.';
            } else if (error.status === 403) {
                return '❌ Error: You do not have permission to use AI features.';
            } else if (error.status === 503) {
                return '❌ Error: AI service is temporarily unavailable. Please try again later.';
            }
            return `❌ Error: ${error.message || 'AI service error'}`;
        }
    }

    /**
     * Query AI chatbot for website tester
     * This chatbot helps test how well the AI can answer questions based on uploaded community documents
     */
    queryWebsiteTesterChatbot(
            question: string,
            contextType: 'documents' | 'general' = 'documents',
            channel: 'website' | 'portal' = 'website',
    ): Observable<AIChatbotResponse> {
        const jwt = LegFiJwtService.read();
        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.AIPennyTesterChatbot(jwt.orgId));

        const requestData = {
            question: question,
            context_type: contextType,
            channel: channel
        };

        return this.applicationHttp.post<PennyTesterResponse>(url, JSON.stringify(requestData)).pipe(
            map((response) => {
                const result: AIChatbotResponse = { success: true, response: response.answer, sources: response.sources || [] };
                return result;
            })
        );
    }
}
